import type { IReportRepository } from '@yawi/domain';
import type {
  DelinquentReport,
  DelinquentParams,
  AccountStatement,
  AccountStatementParams,
  AccountStatementUnit,
  AnnualBillingReport,
  AnnualBillingParams,
  IncomeExpenseReport,
  IncomeExpenseParams,
  MonthlyStatement,
  MonthlyStatementParams,
  Journal,
  JournalParams,
  BankReconciliation,
  BankReconciliationParams,
  ExportFormat,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

export class ApiReportRepository implements IReportRepository {
  private readonly basePath = 'v1/reports';

  // ========== DELINQUENT REPORT ==========

  async getDelinquentReport(params?: DelinquentParams): Promise<DelinquentReport> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.minDaysOverdue) queryParams.set('minDaysOverdue', String(params.minDaysOverdue));
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/delinquent${queryString ? `?${queryString}` : ''}`;

    return await client.get<DelinquentReport>(url);
  }

  async exportDelinquentReport(format: ExportFormat, params?: DelinquentParams): Promise<Blob> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.minDaysOverdue) queryParams.set('minDaysOverdue', String(params.minDaysOverdue));
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/delinquent/export/${format}${queryString ? `?${queryString}` : ''}`;

    return await client.get<Blob>(url, { responseType: 'blob' });
  }

  // ========== ACCOUNT STATEMENT ==========

  async getMyUnits(): Promise<AccountStatementUnit[]> {
    const client = getApiClient();
    const url = `${this.basePath}/account-statement/my/units`;
    return await client.get<AccountStatementUnit[]>(url);
  }

  async getMyAccountStatement(params?: AccountStatementParams): Promise<AccountStatement> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.unitId) queryParams.set('unitId', params.unitId);
    if (params?.fromDate) queryParams.set('fromDate', params.fromDate);
    if (params?.toDate) queryParams.set('toDate', params.toDate);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/account-statement/my${queryString ? `?${queryString}` : ''}`;

    return await client.get<AccountStatement>(url);
  }

  async getUnitAccountStatement(unitId: string, params?: AccountStatementParams): Promise<AccountStatement> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.fromDate) queryParams.set('fromDate', params.fromDate);
    if (params?.toDate) queryParams.set('toDate', params.toDate);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/account-statement/${unitId}${queryString ? `?${queryString}` : ''}`;

    return await client.get<AccountStatement>(url);
  }

  async exportMyAccountStatement(format: ExportFormat, params?: AccountStatementParams): Promise<Blob> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.unitId) queryParams.set('unitId', params.unitId);
    if (params?.fromDate) queryParams.set('fromDate', params.fromDate);
    if (params?.toDate) queryParams.set('toDate', params.toDate);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/account-statement/my/export/${format}${queryString ? `?${queryString}` : ''}`;

    return await client.get<Blob>(url, { responseType: 'blob' });
  }

  async exportUnitAccountStatement(
    unitId: string,
    format: ExportFormat,
    params?: AccountStatementParams
  ): Promise<Blob> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.fromDate) queryParams.set('fromDate', params.fromDate);
    if (params?.toDate) queryParams.set('toDate', params.toDate);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/account-statement/${unitId}/export/${format}${queryString ? `?${queryString}` : ''}`;

    return await client.get<Blob>(url, { responseType: 'blob' });
  }

  // ========== ANNUAL BILLING ==========

  async getAnnualBillingReport(params: AnnualBillingParams): Promise<AnnualBillingReport> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    queryParams.set('year', String(params.year));
    if (params.chargeType) queryParams.set('chargeType', params.chargeType);

    const url = `${this.basePath}/annual-billing?${queryParams.toString()}`;

    return await client.get<AnnualBillingReport>(url);
  }

  async exportAnnualBillingReport(format: ExportFormat, params: AnnualBillingParams): Promise<Blob> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    queryParams.set('year', String(params.year));
    if (params.chargeType) queryParams.set('chargeType', params.chargeType);

    const url = `${this.basePath}/annual-billing/export/${format}?${queryParams.toString()}`;

    return await client.get<Blob>(url, { responseType: 'blob' });
  }

  // ========== INCOME/EXPENSE ==========

  async getIncomeExpenseReport(params: IncomeExpenseParams): Promise<IncomeExpenseReport> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    queryParams.set('year', String(params.year));
    if (params.compareYear) queryParams.set('compareYear', String(params.compareYear));

    const url = `${this.basePath}/income-expense?${queryParams.toString()}`;

    return await client.get<IncomeExpenseReport>(url);
  }

  async exportIncomeExpenseReport(format: ExportFormat, params: IncomeExpenseParams): Promise<Blob> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    queryParams.set('year', String(params.year));
    if (params.compareYear) queryParams.set('compareYear', String(params.compareYear));

    const url = `${this.basePath}/income-expense/export/${format}?${queryParams.toString()}`;

    return await client.get<Blob>(url, { responseType: 'blob' });
  }

  // ========== MONTHLY FINANCIAL STATEMENT ==========

  private buildMonthlyStatementQuery(params: MonthlyStatementParams): URLSearchParams {
    const q = new URLSearchParams();
    q.set('month', params.month);
    if (params.openingBalance !== undefined) q.set('openingBalance', String(params.openingBalance));
    if (params.bankBalance1 !== undefined) q.set('bankBalance1', String(params.bankBalance1));
    if (params.bankLabel1) q.set('bankLabel1', params.bankLabel1);
    if (params.bankBalance2 !== undefined) q.set('bankBalance2', String(params.bankBalance2));
    if (params.bankLabel2) q.set('bankLabel2', params.bankLabel2);
    return q;
  }

  async getMonthlyStatement(params: MonthlyStatementParams): Promise<MonthlyStatement> {
    const client = getApiClient();
    const q = this.buildMonthlyStatementQuery(params);
    return await client.get<MonthlyStatement>(`${this.basePath}/monthly-statement?${q.toString()}`);
  }

  async exportMonthlyStatement(
    format: ExportFormat,
    params: MonthlyStatementParams,
  ): Promise<Blob> {
    const client = getApiClient();
    const q = this.buildMonthlyStatementQuery(params);
    return await client.get<Blob>(
      `${this.basePath}/monthly-statement/export/${format}?${q.toString()}`,
      { responseType: 'blob' },
    );
  }

  // ========== LIBRO DIARIO ==========

  async getJournal(params: JournalParams): Promise<Journal> {
    const client = getApiClient();
    const q = new URLSearchParams();
    q.set('month', params.month);
    if (params.openingBalance !== undefined) q.set('openingBalance', String(params.openingBalance));
    return await client.get<Journal>(`${this.basePath}/journal?${q.toString()}`);
  }

  async exportJournal(format: ExportFormat, params: JournalParams): Promise<Blob> {
    const client = getApiClient();
    const q = new URLSearchParams();
    q.set('month', params.month);
    if (params.openingBalance !== undefined) q.set('openingBalance', String(params.openingBalance));
    return await client.get<Blob>(`${this.basePath}/journal/export/${format}?${q.toString()}`, {
      responseType: 'blob',
    });
  }

  // ========== CONCILIACIÓN BANCARIA ==========

  private buildReconQuery(params: BankReconciliationParams): URLSearchParams {
    const q = new URLSearchParams();
    q.set('month', params.month);
    if (params.openingBalance !== undefined) q.set('openingBalance', String(params.openingBalance));
    if (params.bankBalance !== undefined) q.set('bankBalance', String(params.bankBalance));
    return q;
  }

  async getBankReconciliation(params: BankReconciliationParams): Promise<BankReconciliation> {
    const client = getApiClient();
    const q = this.buildReconQuery(params);
    return await client.get<BankReconciliation>(
      `${this.basePath}/bank-reconciliation?${q.toString()}`,
    );
  }

  async exportBankReconciliation(
    format: ExportFormat,
    params: BankReconciliationParams,
  ): Promise<Blob> {
    const client = getApiClient();
    const q = this.buildReconQuery(params);
    return await client.get<Blob>(
      `${this.basePath}/bank-reconciliation/export/${format}?${q.toString()}`,
      { responseType: 'blob' },
    );
  }

  async reconcilePayment(id: string, reconciled: boolean): Promise<void> {
    const client = getApiClient();
    await client.patch(`v1/payments-v2/${id}/reconcile`, { reconciled });
  }

  async reconcileExpense(id: string, reconciled: boolean): Promise<void> {
    const client = getApiClient();
    await client.patch(`v1/expenses/${id}/reconcile`, { reconciled });
  }
}
